import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, auth } from './auth-context';
import { signOut } from 'firebase/auth';
import SignIn from './sign-in';

const App = () => {
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [isSignInOpen, setIsSignInOpen] = useState(false);
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state?.showSignIn) {
      setIsSignInOpen(true);
    }
  }, [location]);

  const domains = [
    {
      name: 'Security Architecture', value: 18, color: '#38b0e8',
      content: [
        'Network Segmentation',
        'Cloud Security',
        'System Design',
        'IAM Architecture',
        'Security Hardware'
      ],
      path: '/security-architecture'
    },
    {
      name: 'General Security Concepts', value: 12, color: '#FF6B6B',
      content: [
        'CIA Triad',
        'Security Frameworks',
        'Encryption & Hashing',
        'Networking Concepts',
        'Authentication Methods',
      ],
      path: '/general-security-concepts'
    },
    {
      name: 'Security Operations', value: 28, color: '#d68bc6',
      content: [
        'Security Monitoring',
        'Incident Response',
        'Configuration Management',
        'Backup Strategies',
        'Security Automation'
      ],
      path: '/security-operations'
    },
    {
      name: 'Threats, Vulnerabilities and Mitigations', value: 22, color: '#4ECDC4',
      content: [
        'Malware Types',
        'Social Engineering',
        'Network Attacks',
        'Web Vulnerabilities',
        'Zero-day Exploits'
      ],
      path: '/threats-vulnerabilities-mitigations'
    },
    {
      name: 'Security Program Management', value: 20, color: '#FFEEAD',
      content: [
        'Risk Assessment',
        'Security Policies',
        'Awareness Training',
        'Vendor Management',
        'Business Continuity'
      ],
      path: '/program-management'
    },
  ];

  const [isOpen, setIsOpen] = useState(false);

  const checkPaidAccess = async () => {
    if (!user) return false
    // force token refresh to get latest claims
    await user.getIdToken(true)
    return user?.customClaims?.isPaid === true
  }

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const openSignIn = () => {
    setIsSignInOpen(true);
  };

  const closeSignIn = () => {
    setIsSignInOpen(false);
    // If we were redirected from a protected route and user is now authenticated, navigate back to that route
    if (location.state?.from && user) {
      navigate(location.state.from.pathname);
    }
  };

  const handleClick = (entry) => {
    setSelectedDomain(entry.name === selectedDomain ? null : entry.name);
  };

  const handleProtectedNavigation = (path) => {
    if (!user) {
      setIsSignInOpen(true);
      return;
    }
    navigate(path);
  };

  return (
    <div className="min-h-screen bg-[#070B14] text-white">
      {/* Ambient glow */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-40 left-1/2 h-[520px] w-[820px] -translate-x-1/2 rounded-full bg-cyan-500/10 blur-3xl" />
        <div className="absolute top-32 right-[-220px] h-[520px] w-[520px] rounded-full bg-blue-500/10 blur-3xl" />
      </div>

      {/* Navigation Bar */}
      <div className="sticky top-0 z-50 border-b border-white/[0.06] bg-gradient-to-r from-white/[0.04] to-white/[0.02] backdrop-blur-2xl shadow-[inset_0_-1px_0_0_rgba(255,255,255,0.04)]">
        <div className="container mx-auto px-4 lg:px-8 flex justify-between h-16 items-center">
          <div className="flex items-center">
            <Link to="/" className="text-xl font-bold text-cyan-400 flex items-center">
              <img src={require('./mainlogo.png')} alt="CertCrammer" />
            </Link>
          </div>
          <div className="hidden md:flex items-center space-x-1">
            {user ? (
              <button
                onClick={() => handleProtectedNavigation('/practice')}
                className="text-white/70 hover:text-white hover:bg-white/10 px-4 py-2 rounded-xl text-sm monospace transition-colors duration-200"
              >
                Practice Tests
              </button>
            ) : (
              <button
                onClick={openSignIn}
                className="text-white/70 hover:text-white hover:bg-white/10 px-4 py-2 rounded-xl text-sm monospace transition-colors duration-200"
              >
                Practice Tests
              </button>
            )}
            {user ? (
              <button
                onClick={() => handleProtectedNavigation('/flashcards')}
                className="text-white/70 hover:text-white hover:bg-white/10 px-4 py-2 rounded-xl text-sm monospace transition-colors duration-200"
              >
                Flashcards
              </button>
            ) : (
              <button
                onClick={openSignIn}
                className="text-white/70 hover:text-white hover:bg-white/10 px-4 py-2 rounded-xl text-sm monospace transition-colors duration-200"
              >
                Flashcards
              </button>
            )}
            {user ? (
              <button
                onClick={() => handleProtectedNavigation('/reports')}
                className="text-white/70 hover:text-white hover:bg-white/10 px-4 py-2 rounded-xl text-sm monospace transition-colors duration-200"
              >
                Reports
              </button>
            ) : (
              <button
                onClick={openSignIn}
                className="text-white/70 hover:text-white hover:bg-white/10 px-4 py-2 rounded-xl text-sm monospace transition-colors duration-200"
              >
                Reports
              </button>
            )}
            {/* User profile section */}
            {user ? (
              <div className="flex items-center ml-2 pl-2 border-l border-white/10">
                <div className="flex items-center mr-2">
                  <span className="text-white/80 text-sm monospace">{user.displayName}</span>
                </div>
                <button
                  onClick={handleSignOut}
                  className="rounded-xl bg-red-500/80 hover:bg-red-600 text-white px-4 py-1.5 text-sm"
                >
                  <span className="relative z-10">Logout</span>
                </button>
              </div>
            ) : (
              <button
                onClick={openSignIn}
                className="rounded-xl bg-white/10 hover:bg-white/15 ring-1 ring-white/10 text-white px-5 py-2 text-sm ml-2"
              >
                <span className="relative z-10">Sign In</span>
              </button>
            )}
          </div>
          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button onClick={() => setIsOpen(!isOpen)} className="mobile-menu-button p-2.5 text-white rounded-xl bg-white/10 ring-1 ring-white/10 backdrop-blur" aria-label="Menu">
              {isOpen ? (
                <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
            {isOpen && (
              <div className="absolute top-16 right-4 rounded-2xl bg-white/5 p-4 ring-1 ring-white/10 backdrop-blur-sm shadow-2xl w-48 z-[60]">
                <ul className="flex flex-col space-y-2">
                  <li><a href="/practice" className="text-white/80 hover:text-white block py-2">Practice Tests</a></li>
                  <li><a href="/flashcards" className="text-white/80 hover:text-white block py-2">Flashcards</a></li>
                  <li><a href="/reports" className="text-white/80 hover:text-white block py-2">Reports</a></li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Hero section */}
      <div className="relative z-10 overflow-hidden py-16 md:py-20">
        <div className="container mx-auto max-w-[1100px] px-8 lg:px-12">
          <div className="grid md:grid-cols-2 gap-22 items-center">
            {/* Left Side */}
            <div className="space-y-8">
              <div>
                <h1 className="text-4xl md:text-4xl font-bold mb-6 text-white">
                  <span className="bg-gradient-to-r from-cyan-400 to-blue-500 text-transparent bg-clip-text">CompTIA Security+ </span> Assistant
                </h1>
                <p className="text-lg text-white/70 mb-8 leading-relaxed">
                  Master the Security+ faster with an adaptive system that targets your weak spots, reinforces knowledge, and locks in key concepts.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                <button
                  onClick={() => handleProtectedNavigation('/practice')}
                  className="rounded-2xl bg-gradient-to-r from-cyan-500 to-blue-500 hover:opacity-90 px-8 py-3 text-white font-medium text-lg relative overflow-hidden"
                >
                  <span className="relative z-10">Start Practice</span>
                </button>

                <button
                  onClick={() => handleProtectedNavigation('/flashcards')}
                  className="rounded-2xl bg-white/10 hover:bg-white/15 ring-1 ring-white/10 px-8 py-3 text-white font-medium text-lg relative overflow-hidden"
                >
                  <span className="relative z-10">Flashcards</span>
                </button>
              </div>

              <div className="flex flex-col md:flex-row items-center md:items-center md:space-x-6 text-sm text-white/60">
                <div className="flex items-center mb-2 md:mb-0">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-cyan-300 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Adaptive Assessments</span>
                </div>
                <div className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-cyan-300 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Performance Analytics</span>
                </div>
              </div>

            </div>

            {/* Right Side Illustration */}
            <div className="hidden sm:flex justify-center md:justify-end">
              <img
                src={require('./img2.png')}
                className="w-[70%] max-w-md md:max-w-lg lg:max-w-xl h-auto scale-150"
              />
            </div>
          </div>
        </div>
      </div>


      {/* Features section */}
      <div className="relative z-10 py-16 px-6 sm:px-12 lg:px-24">
        <h2 className="text-3xl md:text-4xl text-center mb-12 text-white">
          Fast-Track Your <span className="bg-gradient-to-r from-cyan-400 to-blue-500 text-transparent bg-clip-text">Security+ (SY0-701) </span>Certification
        </h2>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="relative overflow-hidden rounded-2xl bg-white/5 p-6 ring-1 ring-white/10 backdrop-blur shadow-[0_0_0_1px_rgba(34,211,238,0.15),0_20px_80px_rgba(34,211,238,0.06)]">
            <div className="pointer-events-none absolute -top-24 left-1/2 h-48 w-96 -translate-x-1/2 rounded-full bg-cyan-500/10 blur-2xl" />
            <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-2xl bg-[#0B1224] ring-1 ring-white/10">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-cyan-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path><rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect><path d="M9 14l2 2 4-4"></path></svg>
            </div>
            <h3 className="font-semibold text-lg mb-2 text-white monospace">Adaptive Practice Tests</h3>
            <p className="text-sm leading-relaxed text-white/65">Intelligent assessment adapts to your performance, focusing on your weak areas and optimizing study time.</p>
          </div>
          <div className="relative overflow-hidden rounded-2xl bg-white/5 p-6 ring-1 ring-white/10 backdrop-blur">
            <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-2xl bg-[#0B1224] ring-1 ring-white/10">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-cyan-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path></svg>
            </div>
            <h3 className="font-semibold text-lg mb-2 text-white monospace">Interactive Flashcards</h3>
            <p className="text-sm leading-relaxed text-white/65">Reinforces key security concepts with spaced repetition for maximum retention.</p>
          </div>
          <div className="relative overflow-hidden rounded-2xl bg-white/5 p-6 ring-1 ring-white/10 backdrop-blur">
            <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-2xl bg-[#0B1224] ring-1 ring-white/10">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-cyan-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 20V10"></path><path d="M12 20V4"></path><path d="M6 20v-6"></path></svg>
            </div>
            <h3 className="font-semibold text-lg mb-2 text-white monospace">Performance Analytics</h3>
            <p className="text-sm leading-relaxed text-white/65">Track your progress with domain-specific mastery levels and identify areas needing improvement.</p>
          </div>
        </div>
      </div>

      {/* CTA section */}
      <div className="relative z-10 py-16 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="rounded-[2rem] bg-white/5 p-8 sm:p-10 ring-1 ring-white/10">
            <div className="grid items-center gap-8 md:grid-cols-3">
              <div className="md:col-span-2">
                <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-white">
                  Rapidly Memorize <span className="bg-gradient-to-r from-cyan-400 to-blue-500 text-transparent bg-clip-text">Core Competencies</span>
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-white/65">Certcrammer uses neuroscience techniques to optimize learning. These methods adapt to your personal knowledge gaps to ensure maximum retention in minimal time.</p>
              </div>
              <div className="flex md:justify-end">
                {user ? (
                  <button
                    onClick={() => handleProtectedNavigation('/practice')}
                    className="w-full md:w-auto rounded-2xl bg-gradient-to-r from-cyan-500 to-blue-500 hover:opacity-90 px-10 py-3 text-white font-medium text-lg"
                  >
                    Start Learning
                  </button>
                ) : (
                  <button
                    onClick={openSignIn}
                    className="w-full md:w-auto rounded-2xl bg-gradient-to-r from-cyan-500 to-blue-500 hover:opacity-90 px-10 py-3 text-white font-medium text-lg"
                  >
                    Sign in
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Why CertCrammer section */}
      <div className="relative z-10 py-16 px-6 sm:px-12 lg:px-24">
        <div className="container mx-auto max-w-[1100px]">
          <h2 className="text-3xl md:text-4xl text-center mb-4 text-white">
            Why <span className="bg-gradient-to-r from-cyan-400 to-blue-500 text-transparent bg-clip-text">CertCrammer</span>?
          </h2>
          <p className="text-center text-white/50 text-sm mb-12 max-w-2xl mx-auto leading-relaxed">
          </p>

          {/* Comparison grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {/* The problem */}
            <div className="rounded-2xl bg-white/[0.03] p-6 ring-1 ring-white/10">
              <h3 className="text-sm font-semibold text-red-400/80 uppercase tracking-wider mb-5">The alternatives</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-400/60 mr-3 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  <span className="text-sm text-white/60">CompTIA's own learning products: <span className="text-white/80">$700+</span> for Security+ with no adaptive system</span>
                </li>
                <li className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-400/60 mr-3 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  <span className="text-sm text-white/60">Practice exams: Limited questions, no algorithm, often a simple PDF or local application</span>
                </li>
                <li className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-400/60 mr-3 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  <span className="text-sm text-white/60">Bootcamps: <span className="text-white/80">Hundreds to thousands</span> of dollars for a single course</span>
                </li>
                <li className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-400/60 mr-3 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  <span className="text-sm text-white/60">Question dumps: Aimless repetition with no adaptive system, no prioritization, and no real tracking</span>
                </li>
              </ul>
            </div>

            {/* The solution */}
            <div className="rounded-2xl bg-white/[0.03] p-6 ring-1 ring-cyan-500/20 shadow-[0_0_30px_-10px_rgba(6,182,212,0.1)]">
              <h3 className="text-sm font-semibold text-cyan-400/80 uppercase tracking-wider mb-5">CertCrammer</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-cyan-400/70 mr-3 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <span className="text-sm text-white/60">Adaptive algorithm that targets your <span className="text-white/80">actual weak spots</span>, not random rotation</span>
                </li>
                <li className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-cyan-400/70 mr-3 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <span className="text-sm text-white/60">Spaced repetition flashcards that <span className="text-white/80">lock in concepts</span>, not just answers</span>
                </li>
                                <li className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-cyan-400/70 mr-3 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <span className="text-sm text-white/60">Detailed progress reports with domain mastery tracking and targeted study recommendations</span>
                </li>
                <li className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-cyan-400/70 mr-3 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <span className="text-sm text-white/60">Works <span className="text-white/80">anywhere</span> on phone, tablet, or desktop with no downloads needed</span>
                </li>
                <li className="flex items-start">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-cyan-400/70 mr-3 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <span className="text-sm text-white/60">A <span className="text-white/80">fraction of the cost</span> of traditional study materials</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/10 py-8 text-center text-xs text-white/45">
        <p>© 2025 certcrammer. Not affiliated with CompTIA®.</p>
        <div className="flex justify-center space-x-4 mt-2">
          <Link to="/legal" className="hover:text-white/70">Terms of Service</Link>
          <Link to="/legal#privacy" className="hover:text-white/70">Privacy Policy</Link>
          <a href="https://x.com/certcrammer" target="_blank" rel="noopener noreferrer" className="hover:text-white/70">
            Contact Us
          </a>
        </div>
      </footer>

      <SignIn isOpen={isSignInOpen} onClose={closeSignIn} />

    </div>
  );
};

export default App;